import { useStore } from '@store/index';
import { useQuery } from '@tanstack/react-query';
import { getExpiringStorageItem, setExpiringStorageItem } from '@utils/localStorage';
import { removeOrgPrefix, removeProgPrefix } from '@utils/routes';
import { isValidObjectId } from '@utils/validation';
import { useEffect, useState } from 'react';
import { useParams } from 'react-router-dom';

import { organizationService, programService } from '@/services/api';

// Storage expiry for user preferences (14 days)
const STORAGE_EXPIRY_DAYS = 14;

// Comparator to sort entities alphabetically by name
const compareByName = (a: { name?: string }, b: { name?: string }): number => {
  const nameA = a.name || '';
  const nameB = b.name || '';
  return nameA.localeCompare(nameB);
};

// Generate user-specific storage keys
const getStorageKeys = (userId: string | undefined) => ({
  SELECTED_ORG: userId ? `compass_selected_org_id_${userId}` : null,
  SELECTED_PROGRAM: userId ? `compass_selected_program_id_${userId}` : null,
});

// Helper to get the best match
// Priority: url -> case -> localStorage -> first value
const getBestMatch = ({
  data,
  urlId,
  idInCase,
  storageKey,
}: {
  data: string[];
  urlId?: string | null;
  idInCase?: string | null;
  storageKey?: string;
}): string | null => {
  if (urlId && data.includes(urlId)) {
    return urlId;
  }

  if (idInCase && data.includes(idInCase)) {
    return idInCase;
  }

  // Check localStorage if storage key provided
  if (storageKey) {
    const storedId = getExpiringStorageItem(storageKey);
    if (storedId && data.includes(storedId)) {
      return storedId;
    }
  }

  // Fallback to first item
  return data[0] || null;
};

// Hook for URL synchronization
const useUrlSync = () => {
  const {
    orgId: urlOrgId,
    progId: urlProgId,
    caseId,
  } = useParams<{
    orgId?: string;
    progId?: string;
    caseId?: string;
  }>();

  // Remove prefixes from URL params to get clean IDs
  const cleanOrgId = urlOrgId ? removeOrgPrefix(urlOrgId) : undefined;
  const cleanProgId = urlProgId ? removeProgPrefix(urlProgId) : undefined;

  return {
    urlOrgId: cleanOrgId,
    urlProgId: cleanProgId,
    urlCaseId: caseId,
  };
};

// Hook for fetching organizations and programs data
// urlOrgId lets the programs query fire in parallel with orgs
// by reading the org ID from the URL instead of waiting for the store
const useOrgProgramQueries = (urlOrgId: string | undefined) => {
  const { selectedOrganizationId } = useStore();
  const effectiveOrgId = selectedOrganizationId || urlOrgId;

  // Fetch organizations
  const orgsQuery = useQuery({
    queryKey: ['organizations'],
    queryFn: async () => {
      const response = await organizationService.getCurrentUserOrganizations({});
      const orgs = response.organizations || [];
      return [...orgs].sort(compareByName);
    },
    staleTime: 5 * 60 * 1000, // 5 minutes
  });

  // Fetch programs (fires in parallel with orgs when org ID is in the URL)
  const programsQuery = useQuery({
    queryKey: ['programs', effectiveOrgId],
    queryFn: async () => {
      if (!effectiveOrgId) return [];
      const response = await programService.getCurrentUserPrograms({
        organization_id: effectiveOrgId,
      });
      // Ensure we always return an array, never undefined (React Query requirement)
      const programs = response.programs || [];
      return [...programs].sort(compareByName);
    },
    // Only fire when the org id is a real ObjectID. AppStateInitializer runs in
    // ProtectedLayout, ABOVE OrgRouteValidator, so on a URL like /qc it would
    // otherwise fire getCurrentUserPrograms({organization_id:"qc"}) before the
    // validator redirects — a guaranteed 500 (AQ-749).
    enabled: !!effectiveOrgId && isValidObjectId(effectiveOrgId),
    staleTime: 5 * 60 * 1000, // 5 minutes
  });

  const query = {
    data: {
      organizations: orgsQuery.data || [],
      programs: programsQuery.data || [],
    },
    isLoading: orgsQuery.isLoading || programsQuery.isLoading,
    isError: orgsQuery.isError || programsQuery.isError,
    error: orgsQuery.error || programsQuery.error,
  };

  return {
    query,
    orgsQuery,
    programsQuery,
  };
};

/**
 * Unified hook that handles organizations, programs, and URL synchronization
 * Combines functionality from useOrganizations, useProgramsByUser, and useOrgProgramSync
 * Now includes localStorage persistence for selected org/program
 * Priority order: URL -> LocalStorage -> 0th index
 */
export const useOrgAndPrograms = () => {
  const { urlOrgId, urlProgId } = useUrlSync();

  const {
    setOrganizations,
    setPrograms,
    setSelectedOrganizationId,
    setSelectedProgramId,
    selectedOrganizationId,
    selectedProgramId,
    user,
  } = useStore();

  // Get user ID for user-specific storage keys
  const userId = user?.id;
  const storageKeys = getStorageKeys(userId);

  // Fetch organizations and programs (both fire in parallel when org ID is in URL)
  const { query, orgsQuery, programsQuery } = useOrgProgramQueries(urlOrgId);

  // Check for unauthorized access
  const [unauthorizedAccess, setUnauthorizedAccess] = useState<{
    isUnauthorized: boolean;
    orgId?: string;
    progId?: string;
  }>({ isUnauthorized: false });

  // This effect is now handled by the early localStorage check above

  // Save to localStorage when selections change (user-specific)
  useEffect(() => {
    if (!selectedOrganizationId || !storageKeys.SELECTED_ORG) {
      return;
    }
    setExpiringStorageItem(storageKeys.SELECTED_ORG, selectedOrganizationId, STORAGE_EXPIRY_DAYS);
  }, [selectedOrganizationId, storageKeys.SELECTED_ORG]);

  useEffect(() => {
    if (!selectedProgramId || !storageKeys.SELECTED_PROGRAM) {
      return;
    }
    setExpiringStorageItem(storageKeys.SELECTED_PROGRAM, selectedProgramId, STORAGE_EXPIRY_DAYS);
  }, [selectedProgramId, storageKeys.SELECTED_PROGRAM]);

  useEffect(() => {
    if (orgsQuery.data) {
      setOrganizations(orgsQuery.data);
      const userOrgIds = orgsQuery.data.map(o => o.id) as string[];

      setUnauthorizedAccess({ isUnauthorized: false });

      setSelectedOrganizationId(
        getBestMatch({
          data: userOrgIds,
          urlId: urlOrgId,
          idInCase: undefined,
          storageKey: storageKeys.SELECTED_ORG ?? undefined,
        }),
      );
    }
  }, [
    orgsQuery.data,
    setOrganizations,
    urlOrgId,
    setSelectedOrganizationId,
    storageKeys.SELECTED_ORG,
  ]);

  useEffect(() => {
    if (programsQuery.data) {
      setPrograms(programsQuery.data);

      const userProgIds = programsQuery.data.map(p => p.id) as string[];

      setSelectedProgramId(
        getBestMatch({
          data: userProgIds,
          urlId: urlProgId,
          idInCase: undefined,
          storageKey: storageKeys.SELECTED_PROGRAM ?? undefined,
        }),
      );
    }
  }, [
    programsQuery.data,
    setPrograms,
    setSelectedProgramId,
    urlProgId,
    storageKeys.SELECTED_PROGRAM,
  ]);

  return {
    ...query,
    organizations: orgsQuery.data || [],
    programs: programsQuery.data || [],
    isLoadingOrgAndPrograms: orgsQuery.isLoading || programsQuery.isLoading,
    selectedOrganizationId,
    selectedProgramId,
    unauthorizedAccess,
  };
};
